import { useTranslation } from "react-i18next";
import cn from "classnames";

import { ChartsProps } from "./Charts.types";

import {
  getAquisitionsPrice,
  getEarlySaleProfit,
  getTotalTaxSavings,
  getTotalInvestment,
} from "./Charts.helpers";

import {
  getLoanProfitChartData,
  getLoanRateChartData,
} from "components/CashFlowConclusion/CashFlowConclusion.helpers";

import { Typography } from "shared/ui/components/Typography";

import { Conclusions } from "components/Conclusions";
// import { CashFlowConclusion } from "components/CashFlowConclusion"; //TODO Remove after new design confirmation
import { InvestmentBasicData } from "components/InvestmentBasicData"; //TODO Remove after new design confirmation
// import { InvestmentConclusion } from "components/InvestmentConclusion"; //TODO Remove after new design confirmation

import { LoanProfitChart } from "components/CashFlowConclusion/components/LoanProfitChart";
import { LoanRateChart } from "components/CashFlowConclusion/components/LoanRateChart";

import { IrrCard } from "../IrrCard";
import { InfoCard } from "../InfoCard";
import { CostChart } from "../CostChart";
import { CashFlowChart } from "../CashFlowChart";
import { RentalYieldCard } from "../RentalYieldCard";
import { RentalIncomeCard } from "../RentalIncomeCard";
import { PropertyValueCard } from "../PropertyValueCard";
import { AuthMessage } from "../AuthMessage";

import styles from "./Charts.module.scss";

const _renderInfoPopover = ({ info }: { info: any }) => {
  return (
    <div className={styles.popover}>
      <Typography variant="bodyS" weight="light">
        {info?.section1}
      </Typography>
      {info?.section2 && (
        <Typography
          variant="bodyS"
          weight="light"
          className={styles.popoverSection}
        >
          {info?.section2}
        </Typography>
      )}
    </div>
  );
};

const Charts = ({
  isAuthenticated,
  post,
  calculation,
  conclusionsPro,
  proCalculation,
  proCalculationCharts,
  activePosition,
  energyEfficiencyClass,
  onPositionChange,
}: ChartsProps) => {
  const { t } = useTranslation();

  const userScenarioResult = conclusionsPro?.userScenarioResult;

  const loanProfitChartData = getLoanProfitChartData({
    activePosition,
    conclusionsPro,
    userScenarioResult,
    proCalculation,
  });

  const loanRateChartData = getLoanRateChartData({
    activePosition,
    conclusionsPro,
    userScenarioResult,
    proCalculation,
  });

  return (
    <div className={styles.content}>
      <div className={styles.chartsWrapper}>
        <InfoCard
          text={t("calculator_pro_page.analytics.info_card.net_profit", {
            years: activePosition,
          })}
          amount={getEarlySaleProfit({ proCalculationCharts, activePosition })}
          infoPopover={
            <div className={styles.chartListPopover}>
              <Typography variant="bodyS" weight="medium">
                {t("calculator_pro_page.analytics.early_sale_profit_tooltip")}
              </Typography>
            </div>
          }
        />
        <InfoCard
          text={t("calculator_pro_page.analytics.info_card.total_tax_savings", {
            years: activePosition,
          })}
          amount={getTotalTaxSavings({ proCalculationCharts, activePosition })}
          infoPopover={
            <div className={styles.chartListPopover}>
              <Typography variant="bodyS" weight="medium">
                {t("calculator_pro_page.analytics.total_tax_savings_tooltip")}
              </Typography>
            </div>
          }
        />
        <InfoCard
          text={t("calculator_pro_page.analytics.info_card.initial_investment")}
          amount={getAquisitionsPrice(proCalculationCharts?.costsChart)}
          infoPopover={
            <CostChart costsChart={proCalculationCharts.costsChart} />
          }
        />
        <InfoCard
          text={t("calculator_pro_page.analytics.info_card.total_investment", {
            years: activePosition,
          })}
          amount={getTotalInvestment({ proCalculationCharts, activePosition })}
          infoPopover={
            <div className={styles.chartListPopover}>
              <Typography variant="bodyS" weight="medium">
                {t("calculator_pro_page.analytics.investments_acc_tooltip")}
              </Typography>
            </div>
          }
        />
      </div>

      <div className={styles.chartsWrapper}>
        <div className={styles.chartItem}>
          <div className={styles.chartItems}>
            <RentalYieldCard calculation={calculation} />
            <RentalIncomeCard
              totalIncomeChart={proCalculationCharts.totalIncomeChart}
              activePosition={activePosition}
            />
            <PropertyValueCard
              propertyValueChart={proCalculationCharts.propertyValueChart}
              activePosition={activePosition}
            />
            <IrrCard
              conclusionsPro={conclusionsPro}
              userScenarioResult={userScenarioResult}
              activePosition={activePosition}
            />
          </div>
        </div>
        <div className={styles.chartItem}>
          <CashFlowChart
            activePosition={activePosition}
            conclusionsPro={conclusionsPro}
          />
          <div className={styles.chartItems}>
            <LoanProfitChart
              title={t("loan_conclusion.monthly_payment.title")}
              labelTextColor="#131618"
              variant={
                loanProfitChartData.overwriteVariant || userScenarioResult
              }
              descriptionVariant={
                loanProfitChartData.overwriteVariant || userScenarioResult
              }
              caption={loanProfitChartData.caption}
              details={loanProfitChartData.details}
              price={loanProfitChartData.price}
              description={loanProfitChartData?.description || ""}
              data={loanProfitChartData.chartData}
              infoPopover={_renderInfoPopover({
                info: loanProfitChartData.info,
              })}
            />
            <LoanRateChart
              title={t("loan_conclusion.loan_rate.title")}
              labelTextColor="#ffffff"
              variant={loanRateChartData.overwriteVariant || userScenarioResult}
              price={loanRateChartData.price}
              caption={loanRateChartData.caption}
              details={loanRateChartData.details}
              description={loanRateChartData?.description || ""}
              data={loanRateChartData.chartData}
              infoPopover={_renderInfoPopover({ info: loanRateChartData.info })}
            />
          </div>
        </div>
      </div>

      <div className={styles.category}>
        <div className={styles.categoryHeader}>
          <Typography
            className={styles.categoryTitle}
            variant="h5"
            weight="semiBold"
          >
            Ihre Ergebnisse im Vergleich – mit und ohne Förderung
          </Typography>
        </div>

        {isAuthenticated && (
          <Conclusions
            className={styles.conclusions}
            activePosition={activePosition}
            calculation={calculation}
            conclusionsPro={conclusionsPro}
            proCalculation={proCalculation}
            proCalculationCharts={proCalculationCharts}
            energyEfficiencyClass={energyEfficiencyClass}
          />
        )}
        {!isAuthenticated && <AuthMessage />}
      </div>

      <div className={styles.category}>
        <div className={cn(styles.categoryHeader, styles.categoryHeaderCustom)}>
          <Typography
            className={styles.categoryTitle}
            variant="h5"
            weight="semiBold"
          >
            {t("calculator_pro_page.analytics.summary")}
          </Typography>
        </div>
        <InvestmentBasicData
          className={styles.investmentBasicData}
          withBanner
          post={post}
          activePosition={activePosition}
          onPositionChange={onPositionChange}
          calculation={calculation}
          proCalculationCharts={proCalculationCharts}
          conclusionsPro={conclusionsPro}
        />
      </div>

      {/* <div className={styles.category}>
        <div className={styles.categoryHeader}>
          <Typography
            className={styles.categoryTitle}
            variant="h3"
            weight="semiBold"
          >
            {t("calculator_pro_page.analytics.general")}
          </Typography>
        </div>
        <InvestmentConclusion
          calculation={calculation}
          proCalculation={proCalculation}
          proCalculationCharts={proCalculationCharts}
          activePosition={activePosition}
          energyEfficiencyClass={energyEfficiencyClass}
          conclusionsPro={conclusionsPro}
        />
      </div> */}
      {/* {conclusionsPro && (
        <>
          <div className={styles.category}>
            <div className={styles.categoryHeader}>
              <Typography
                className={styles.categoryTitle}
                variant="h3"
                weight="semiBold"
              >
                {t("calculator_pro_page.analytics.cashflow_and_credit")}
              </Typography>
            </div>
            <CashFlowConclusion
              activePosition={activePosition}
              calculation={calculation}
              proCalculation={proCalculation}
              proCalculationCharts={proCalculationCharts}
              conclusionsPro={conclusionsPro}
              energyEfficiencyClass={energyEfficiencyClass}
            />
          </div>
        </>
      )} */}
    </div>
  );
};

export default Charts;
